import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import multiplayerService from '@/utils/multiplayerService';
import { 
  Users, Copy, Check, Loader2, Link2, 
  Crown, Swords, UserPlus, Share2
} from 'lucide-react';

const MultiplayerDialog = ({ 
  isOpen, 
  onClose, 
  theme, 
  onStartMultiplayer,
  initialRoomId = null 
}) => {
  const { user, isAuthenticated } = useAuth();
  const [mode, setMode] = useState(initialRoomId ? 'join' : 'menu'); // menu, create, join, waiting
  const [playerName, setPlayerName] = useState('');
  const [selectedColor, setSelectedColor] = useState('white');
  const [roomId, setRoomId] = useState(initialRoomId || '');
  const [joinUrl, setJoinUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roomInfo, setRoomInfo] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      setPlayerName(user.name || '');
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (initialRoomId && isOpen) {
      setRoomId(initialRoomId);
      setMode('join');
      loadRoomInfo(initialRoomId);
    }
  }, [initialRoomId, isOpen]);

  const loadRoomInfo = async (id) => {
    try {
      const info = await multiplayerService.getRoomInfo(id);
      setRoomInfo(info);
    } catch (e) {
      setError('Room not found or has expired');
    }
  };

  const handleCreateRoom = async () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { room_id, join_url } = await multiplayerService.createRoom(
        playerName.trim(),
        selectedColor
      );
      setRoomId(room_id);
      setJoinUrl(join_url);
      setMode('waiting');
    } catch (e) {
      setError('Failed to create room. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRoom = async () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!roomId.trim()) {
      setError('Please enter a room code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await multiplayerService.joinRoom(roomId.trim(), playerName.trim());
      // Start the game
      onStartMultiplayer({
        roomId: roomId.trim(),
        playerColor: result.your_color,
        playerName: playerName.trim(),
        opponentName: result.host_name,
        isHost: false
      });
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to join room');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(joinUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStartWaiting = () => {
    // Connect to WebSocket and wait for opponent
    onStartMultiplayer({
      roomId,
      playerColor: selectedColor,
      playerName: playerName.trim(),
      opponentName: null,
      isHost: true
    });
    onClose();
  };

  const renderMenu = () => (
    <div className="space-y-4">
      <div className={`text-center py-4 ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-600'}`}>
        <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>Play chess with a friend by sharing a link</p>
      </div>

      <div className="space-y-3">
        <Button
          onClick={() => setMode('create')}
          className={`w-full py-6 text-lg ${
            theme === 'cosmic'
              ? 'bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500'
              : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400'
          }`}
        >
          <Crown className="w-5 h-5 mr-2" />
          Create Game
        </Button>

        <Button
          onClick={() => setMode('join')}
          variant="outline"
          className={`w-full py-6 text-lg ${
            theme === 'cosmic' ? 'border-slate-600 hover:bg-slate-800' : ''
          }`}
        >
          <UserPlus className="w-5 h-5 mr-2" />
          Join Game
        </Button>
      </div>
    </div>
  );

  const renderCreate = () => (
    <div className="space-y-4">
      <div>
        <label className={`text-sm ${theme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
          Your Name
        </label>
        <Input
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          placeholder="Enter your name"
          className={theme === 'cosmic' ? 'bg-slate-800 border-slate-600' : ''}
        />
      </div>

      <div>
        <label className={`text-sm ${theme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
          Play as
        </label>
        <div className="flex gap-2 mt-2">
          {['white', 'black'].map((color) => (
            <button
              key={color}
              onClick={() => setSelectedColor(color)}
              className={`
                flex-1 py-3 rounded-lg font-medium transition-all capitalize
                ${selectedColor === color
                  ? color === 'white'
                    ? 'bg-white text-gray-900 border-2 border-cyan-400'
                    : 'bg-gray-900 text-white border-2 border-cyan-400'
                  : theme === 'cosmic'
                    ? 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
              `}
            >
              {color}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          variant="outline"
          onClick={() => setMode('menu')}
          className={theme === 'cosmic' ? 'border-slate-600' : ''}
        >
          Back
        </Button>
        <Button
          onClick={handleCreateRoom}
          disabled={loading}
          className={`flex-1 ${
            theme === 'cosmic'
              ? 'bg-cyan-600 hover:bg-cyan-700'
              : 'bg-amber-600 hover:bg-amber-700'
          }`}
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Room'}
        </Button>
      </div>
    </div>
  );

  const renderWaiting = () => (
    <div className="space-y-4 text-center">
      <div className={`py-4 ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-600'}`}>
        <div className="relative w-20 h-20 mx-auto mb-4">
          <div className="absolute inset-0 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          <Users className="absolute inset-0 m-auto w-8 h-8" />
        </div>
        <p className="text-lg font-medium">Waiting for opponent...</p>
        <p className="text-sm opacity-70">Share this link with a friend</p>
      </div>

      <div className={`
        p-4 rounded-xl
        ${theme === 'cosmic' ? 'bg-slate-800 border border-slate-700' : 'bg-gray-100'}
      `}>
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
          <input
            value={joinUrl}
            readOnly
            className={`
              flex-1 bg-transparent text-sm truncate
              ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}
            `}
          />
          <Button
            size="sm"
            onClick={handleCopyLink}
            className={theme === 'cosmic' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className={`text-xs ${theme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
        Room Code: <span className="font-mono font-bold">{roomId}</span>
      </div>

      <div className="flex gap-2 pt-2">
        <Button
          variant="outline"
          onClick={() => {
            setMode('menu');
            setRoomId('');
            setJoinUrl('');
          }}
          className={theme === 'cosmic' ? 'border-slate-600' : ''}
        >
          Cancel
        </Button>
        <Button
          onClick={handleStartWaiting}
          className={`flex-1 ${
            theme === 'cosmic'
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          <Share2 className="w-4 h-4 mr-2" />
          Start & Wait
        </Button>
      </div>
    </div>
  );

  const renderJoin = () => (
    <div className="space-y-4">
      <div>
        <label className={`text-sm ${theme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
          Your Name
        </label>
        <Input
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          placeholder="Enter your name"
          className={theme === 'cosmic' ? 'bg-slate-800 border-slate-600' : ''}
        />
      </div>

      <div>
        <label className={`text-sm ${theme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
          Room Code
        </label>
        <Input
          value={roomId}
          onChange={(e) => setRoomId(e.target.value)}
          placeholder="Enter room code"
          className={`font-mono ${theme === 'cosmic' ? 'bg-slate-800 border-slate-600' : ''}`}
        />
      </div>

      {roomInfo && (
        <div className={`
          p-3 rounded-lg
          ${theme === 'cosmic' ? 'bg-slate-800 border border-slate-700' : 'bg-gray-100'}
        `}>
          <p className={`text-sm ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="opacity-70">Host:</span> {roomInfo.host_name}
          </p>
          <p className={`text-sm ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="opacity-70">You'll play as:</span>{' '}
            {roomInfo.host_color === 'white' ? 'Black' : 'White'}
          </p>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          variant="outline"
          onClick={() => {
            setMode('menu');
            setRoomId('');
            setRoomInfo(null);
            setError('');
          }}
          className={theme === 'cosmic' ? 'border-slate-600' : ''}
        >
          Back
        </Button>
        <Button
          onClick={handleJoinRoom}
          disabled={loading}
          className={`flex-1 ${
            theme === 'cosmic'
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Join Game'}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`
        max-w-md
        ${theme === 'cosmic'
          ? 'bg-slate-900 border-slate-700 text-white'
          : 'bg-white'
        }
      `}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className={theme === 'cosmic' ? 'text-cyan-400' : 'text-amber-600'} />
            Play with Friend
          </DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {mode === 'menu' && renderMenu()}
            {mode === 'create' && renderCreate()}
            {mode === 'waiting' && renderWaiting()}
            {mode === 'join' && renderJoin()}
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default MultiplayerDialog;
